/**
 * Paleta de comandos (Ctrl/Cmd+K ou a busca do cabeçalho): filtra os editais já carregados (objeto/órgão) e oferece
 * ações (ir para uma seção, filtrar o quadro, marcar notificações como lidas). Setas + Enter escolhem; Esc fecha.
 * Tudo no cliente, sem chamada nova à API além da lista "todos" (que o quadro já usa).
 */
import type { AppEdital } from '@licita/contracts';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { editaisTodosQuery, marcarLidas, notificacoesQuery, prazo } from '../lib/app';
import { IconBell, IconBoard, IconBuilding, IconContract, IconDoc, IconList, IconSearch } from './icons';
import { Chip, NotaRing } from './ui';

interface Item {
  id: string;
  grupo: 'acoes' | 'editais';
  icone: ReactNode;
  titulo: string;
  detalhe?: string;
  extra?: ReactNode;
  executar: () => void | Promise<void>;
}

const normalizar = (s: string) => s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

const MAX_EDITAIS = 8;

export function Paleta({ onFechar }: { onFechar: () => void }) {
  const navigate = useNavigate();
  const [texto, setTexto] = useState('');
  const [ativo, setAtivo] = useState(0);
  const ref = useRef<HTMLDialogElement>(null);
  const lista = useRef<HTMLUListElement>(null);
  const id = useId();
  const editais = useQuery(editaisTodosQuery);
  const notificacoes = useQuery({ ...notificacoesQuery, enabled: false });

  useEffect(() => {
    const d = ref.current;
    if (d && !d.open) d.showModal();
  }, []);

  const fechar = () => ref.current?.close();
  const ir = (fn: () => Promise<unknown>) => {
    fechar();
    void fn();
  };

  const q = normalizar(texto.trim());
  const itens = useMemo<Item[]>(() => {
    const acoes: Item[] = [
      {
        id: 'quadro',
        grupo: 'acoes',
        icone: <IconBoard size={15} />,
        titulo: 'Ir para o Quadro',
        executar: () => ir(() => navigate({ to: '/', search: {} })),
      },
      {
        id: 'lista',
        grupo: 'acoes',
        icone: <IconList size={15} />,
        titulo: 'Ir para a Lista',
        executar: () => ir(() => navigate({ to: '/', search: { vista: 'lista' } })),
      },
      {
        id: 'contratos',
        grupo: 'acoes',
        icone: <IconContract size={15} />,
        titulo: 'Ir para Contratos',
        executar: () => ir(() => navigate({ to: '/contratos' })),
      },
      {
        id: 'empresa',
        grupo: 'acoes',
        icone: <IconBuilding size={15} />,
        titulo: 'Ir para Empresa',
        executar: () => ir(() => navigate({ to: '/empresa' })),
      },
      {
        id: 'lidas',
        grupo: 'acoes',
        icone: <IconBell size={15} />,
        titulo: 'Marcar notificações como lidas',
        detalhe: notificacoes.data ? `${notificacoes.data.naoLidas} não lidas` : undefined,
        executar: () => ir(() => marcarLidas()),
      },
    ];
    if (q) {
      acoes.unshift({
        id: 'buscar',
        grupo: 'acoes',
        icone: <IconSearch size={15} />,
        titulo: `Filtrar o quadro por "${texto.trim()}"`,
        executar: () => ir(() => navigate({ to: '/', search: (prev) => ({ ...prev, q: texto.trim() }) })),
      });
    }
    const filtradas = q ? acoes.filter((a) => a.id === 'buscar' || normalizar(a.titulo).includes(q)) : acoes;
    const todos = editais.data?.itens ?? [];
    const achados = (q ? todos.filter((e) => normalizar(`${e.objeto} ${e.orgao} ${e.numeroControlePncp}`).includes(q)) : todos).slice(
      0,
      MAX_EDITAIS,
    );
    const cards: Item[] = achados.map((e) => ({
      id: e.fitId,
      grupo: 'editais',
      icone: <NotaRing nota={e.nota} size={24} />,
      titulo: e.objeto,
      detalhe: `${e.orgao}${e.uf ? ` · ${e.uf}` : ''}`,
      extra: <PrazoChip e={e} />,
      executar: () =>
        ir(() => navigate({ to: '/editais/$fitId', params: { fitId: e.fitId }, search: (prev) => ({ ...prev, aba: 'dados' }) })),
    }));
    return [...filtradas, ...cards];
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `navigate` e `ir` são estáveis para a vida da paleta
  }, [q, texto, editais.data, notificacoes.data]);

  // O item ativo acompanha a lista (não sai do intervalo) e fica visível.
  useEffect(() => setAtivo(0), [q]);
  useEffect(() => {
    lista.current?.querySelector<HTMLElement>(`[data-i="${ativo}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [ativo]);

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setAtivo((i) => Math.min(itens.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setAtivo((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      void itens[ativo]?.executar();
    }
  };

  const ativoId = itens[ativo] ? `${id}-${itens[ativo].id}` : undefined;
  let grupoAnterior: Item['grupo'] | null = null;

  return (
    <dialog
      ref={ref}
      aria-label="Buscar e executar ações"
      onClose={onFechar}
      onCancel={(e) => {
        e.preventDefault();
        fechar();
      }}
      onClick={(e) => {
        if (e.target === ref.current) fechar();
      }}
      className="app-pop mt-[8vh] mb-auto w-[calc(100%-32px)] max-w-[600px] p-0 text-fg backdrop:bg-black/50 max-h-[calc(100dvh-16vh)] lg:mt-[12vh]"
    >
      <div className="flex items-center gap-2 border-b border-line px-3">
        <span className="text-mute">
          <IconSearch size={16} />
        </span>
        <label htmlFor={`${id}-input`} className="sr-only">
          Buscar edital, órgão ou ação
        </label>
        <input
          id={`${id}-input`}
          type="text"
          role="combobox"
          aria-expanded="true"
          aria-controls={`${id}-lista`}
          aria-activedescendant={ativoId}
          aria-autocomplete="list"
          autoComplete="off"
          autoFocus
          className="h-12 flex-1 bg-transparent text-[15px] outline-none placeholder:text-mute"
          placeholder="Buscar edital, órgão ou ação…"
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          onKeyDown={onKey}
        />
        <kbd className="rounded-[5px] border border-line px-1.5 py-0.5 font-mono text-[10px] text-mute">Esc</kbd>
      </div>
      <ul id={`${id}-lista`} ref={lista} role="listbox" aria-label="Resultados" className="max-h-[min(60vh,440px)] overflow-y-auto p-1.5">
        {itens.length === 0 && (
          <li className="px-3 py-6 text-center text-[13px] text-mute" role="presentation">
            {editais.isPending ? 'Carregando editais…' : 'Nada encontrado.'}
          </li>
        )}
        {itens.map((it, i) => {
          const cabecalho = it.grupo !== grupoAnterior;
          grupoAnterior = it.grupo;
          return (
            <li key={it.id} role="presentation">
              {cabecalho && (
                <div className="app-lbl px-2.5 pt-2 pb-1" role="presentation">
                  {it.grupo === 'acoes' ? 'Ações' : 'Editais'}
                </div>
              )}
              <div
                id={`${id}-${it.id}`}
                role="option"
                aria-selected={i === ativo}
                data-i={i}
                className={`flex cursor-pointer items-center gap-3 rounded-lg px-2.5 py-2 text-[13px] ${i === ativo ? 'bg-bg-3 text-fg' : 'text-fg-2'}`}
                onMouseMove={() => setAtivo(i)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => void it.executar()}
              >
                <span className={`shrink-0 ${it.grupo === 'acoes' ? 'text-accent' : ''}`}>{it.icone}</span>
                <span className="min-w-0 flex-1">
                  <span className="line-clamp-1 font-medium text-fg">{it.titulo}</span>
                  {it.detalhe && <span className="line-clamp-1 text-[12px] text-mute">{it.detalhe}</span>}
                </span>
                {it.extra}
              </div>
            </li>
          );
        })}
      </ul>
      <div className="flex items-center gap-3 border-t border-line px-3 py-2 text-[11px] text-mute">
        <span>
          <kbd className="font-mono">↑↓</kbd> navegar
        </span>
        <span>
          <kbd className="font-mono">Enter</kbd> abrir
        </span>
        <span className="ml-auto inline-flex items-center gap-1">
          <IconDoc size={12} /> {editais.data ? `${editais.data.itens.length} editais` : ''}
        </span>
      </div>
    </dialog>
  );
}

function PrazoChip({ e }: { e: AppEdital }) {
  const p = prazo(e.encerramentoPropostasEm);
  return (
    <Chip tone={p.tone} mono>
      {p.curto}
    </Chip>
  );
}
